import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  changepassword,
  cloneVersion,
  createproject,
  createrequirementtype,
  createtestcasepriority,
  createtestcasestatus,
  createworkspace,
  createworkspaceversion,
  deleteprojectbyid,
  deleterequirementtypebyId,
  deletetestcaseprioritiesbyId,
  deletetestcasestatusbyId,
  deleteVersion,
  deleteWorkspace,
  updateproject,
  updaterequirementtypeById,
  updatetestcasepriorityById,
  updatetestcasestatusById,
  updateUserById,
  updateVersion,
  updateWorkspace,
} from "./settings.api";

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload }: { payload: any }) => createproject(payload),
    onMutate: async ({ payload }: { payload: any }) => {
      // Pre-check: if we already know the limit is hit from cached usage, fire
      // the paywall event immediately and abort — no flicker, no round-trip.
      const usage = queryClient.getQueryData<any>(["billing", "usage"]);
      const maxProjects = usage?.data?.limits?.maxProjects;
      const usedProjects = usage?.data?.used?.projects;
      if (
        maxProjects != null &&
        usedProjects != null &&
        usedProjects >= maxProjects
      ) {
        window.dispatchEvent(
          new CustomEvent("paywall:open", {
            detail: {
              error: "upgrade_required",
              feature: "projects",
              limit: maxProjects,
              used: usedProjects,
              upgrade_url: "/billing",
            },
          }),
        );
        throw new Error("upgrade_required");
      }

      await queryClient.cancelQueries({
        queryKey: ["projectList"],
        exact: false,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["projectList"],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ["billing", "usage"] });
    },
  });
};
const useChangePassword = () => {
  return useMutation({
    mutationFn: ({ payload }: { payload: any }) => changepassword(payload),
  });
};
export { useChangePassword as usechangepassword };
type UpdateProjectInput = {
  id: number;
  payload: any;
  files?: File[];
};
export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: UpdateProjectInput) =>
      updateproject(id, payload),
    onMutate: async ({ id, payload }: UpdateProjectInput) => {
      // Cancel any outgoing refetches for both list and detail queries
      await queryClient.cancelQueries({
        queryKey: ["projectList"],
        exact: false,
      });
      await queryClient.cancelQueries({ queryKey: ["projectById", id] });

      const previousData = queryClient.getQueriesData({
        queryKey: ["projectList"],
        exact: false,
      });
      const previousProjectById = queryClient.getQueryData(["projectById", id]);

      // Optimistically update project list
      queryClient.setQueriesData(
        { queryKey: ["projectList"], exact: false },
        (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                content: (page.data?.content ?? []).map((p: any) =>
                  p.id === id ? { ...p, ...payload } : p,
                ),
              },
            })),
          };
        },
      );

      // Optimistically update the single-project detail cache so the overview
      // reflects the new value immediately (no stale-data flash)
      queryClient.setQueryData(["projectById", id], (old: any) => {
        if (!old) return old;
        // Handle both { data: project } and bare project shapes
        if (old?.data) return { ...old, data: { ...old.data, ...payload } };
        return { ...old, ...payload };
      });

      return { previousData, previousProjectById };
    },
    onError: (_err, { id }, context: any) => {
      context?.previousData?.forEach(([key, value]: any) =>
        queryClient.setQueryData(key, value),
      );
      if (context?.previousProjectById !== undefined) {
        queryClient.setQueryData(
          ["projectById", id],
          context.previousProjectById,
        );
      }
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({
        queryKey: ["projectList"],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ["projectById", id] });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteprojectbyid(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({
        queryKey: ["projectList"],
        exact: false,
      });
      const previousData = queryClient.getQueriesData({
        queryKey: ["projectList"],
        exact: false,
      });
      queryClient.setQueriesData(
        { queryKey: ["projectList"], exact: false },
        (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                content: (page.data?.content ?? []).filter(
                  (p: any) => p.id !== id,
                ),
              },
            })),
          };
        },
      );
      return { previousData };
    },
    onError: (_err, _vars, context: any) => {
      context?.previousData?.forEach(([key, value]: any) =>
        queryClient.setQueryData(key, value),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["projectList"],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "usage"] });
    },
  });
};
export const useDeletetestcasepriorityById = (testcasepriorityid: number) => {
  return useMutation({
    mutationFn: () => deletetestcaseprioritiesbyId(testcasepriorityid),
  });
};
export const useCreateTestCasePriority = () => {
  return useMutation({
    mutationFn: (payload: any) => createtestcasepriority(payload),
  });
};
const useUpdateTestCasePriorityById = (prioritiesid: number) => {
  return useMutation({
    mutationFn: (payload: any) =>
      updatetestcasepriorityById(prioritiesid, payload),
  });
};
export { useUpdateTestCasePriorityById as useupdatetestcasepriorityById };

export const useDeletetestcasestatusById = (statusId: number) => {
  return useMutation({
    mutationFn: () => deletetestcasestatusbyId(statusId),
  });
};
export const useCreateTestCaseStatus = () => {
  return useMutation({
    mutationFn: (payload: any) => createtestcasestatus(payload),
  });
};
const useUpdateTestCaseStatusById = (statusId: number) => {
  return useMutation({
    mutationFn: (payload: any) => updatetestcasestatusById(statusId, payload),
  });
};
export { useUpdateTestCaseStatusById as useupdatetestcasestatusById };

export const useDeleterequirementtypeById = (requirementtypeid: number) => {
  return useMutation({
    mutationFn: () => deleterequirementtypebyId(requirementtypeid),
  });
};
export const useCreateRequirementType = () => {
  return useMutation({
    mutationFn: (payload: any) => createrequirementtype(payload),
  });
};
const useUpdateRequirementTypeById = (prioritiesid: number) => {
  return useMutation({
    mutationFn: (payload: any) =>
      updaterequirementtypeById(prioritiesid, payload),
  });
};
export { useUpdateRequirementTypeById as useupdaterequirementtypeById };
export const useCreateworkspaceversion = () => {
  return useMutation({
    mutationFn: (payload: any) => createworkspaceversion(payload),
  });
};

export const useCreateworkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => createworkspace(payload),
    onMutate: async (payload: any) => {
      const projectId = payload.projectId;
      if (!projectId) return;
      const strProjectId = String(projectId);

      await queryClient.cancelQueries({
        queryKey: ["workspaceVersionsList", strProjectId],
      });
      await queryClient.cancelQueries({
        queryKey: ["workspacesByProject", projectId],
      });
      await queryClient.cancelQueries({
        queryKey: ["projectOverview", projectId],
      });

      const prevVersionsList = queryClient.getQueryData([
        "workspaceVersionsList",
        strProjectId,
      ]);
      const prevWorkspacesByProject = queryClient.getQueryData([
        "workspacesByProject",
        projectId,
      ]);
      const prevOverview = queryClient.getQueryData([
        "projectOverview",
        projectId,
      ]);

      const tempId = Date.now();
      const optimisticWs = {
        id: tempId,
        name: payload.name,
        projectId: payload.projectId,
        workspaceType: payload.workspaceType || "WebApplication",
        type: payload.workspaceType || "WebApplication",
        platform: payload.workspaceType || "WebApplication",
        versionName: payload.versionName || "v1.0",
        createdDate: new Date().toISOString(),
        updatedDate: new Date().toISOString(),
        cases: 0,
        requirements: 0,
        cycles: 0,
        releases: 0,
        coverage: 0,
        automation: 0,
        passRate: 0,
        active: false,
        integrations: [],
      };

      if (prevVersionsList) {
        queryClient.setQueryData(
          ["workspaceVersionsList", strProjectId],
          (old: any) => {
            if (!old) return old;
            const content = Array.isArray(old?.data?.content)
              ? old.data.content
              : Array.isArray(old?.data)
                ? old.data
                : [];
            const updatedContent = [...content, optimisticWs];
            return old?.data?.content
              ? { ...old, data: { ...old.data, content: updatedContent } }
              : { ...old, data: updatedContent };
          },
        );
      }

      if (prevWorkspacesByProject) {
        queryClient.setQueryData(
          ["workspacesByProject", projectId],
          (old: any) => {
            if (!old) return old;
            const content = Array.isArray(old?.data?.content)
              ? old.data.content
              : Array.isArray(old?.data)
                ? old.data
                : [];
            const updatedContent = [...content, optimisticWs];
            return old?.data?.content
              ? { ...old, data: { ...old.data, content: updatedContent } }
              : { ...old, data: updatedContent };
          },
        );
      }

      if (prevOverview) {
        queryClient.setQueryData(["projectOverview", projectId], (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              workspaces: [...(old.data.workspaces || []), optimisticWs],
            },
          };
        });
      }

      return {
        prevVersionsList,
        prevWorkspacesByProject,
        prevOverview,
        strProjectId,
        projectId,
      };
    },
    onError: (_err, _payload, context: any) => {
      if (context?.prevVersionsList) {
        queryClient.setQueryData(
          ["workspaceVersionsList", context.strProjectId],
          context.prevVersionsList,
        );
      }
      if (context?.prevWorkspacesByProject) {
        queryClient.setQueryData(
          ["workspacesByProject", context.projectId],
          context.prevWorkspacesByProject,
        );
      }
      if (context?.prevOverview) {
        queryClient.setQueryData(
          ["projectOverview", context.projectId],
          context.prevOverview,
        );
      }
    },
    onSettled: (_data, _error, payload: any) => {
      const projectId = payload?.projectId;
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: ["workspaceVersionsList", String(projectId)],
        });
        queryClient.invalidateQueries({
          queryKey: ["workspacesByProject", projectId],
        });
        queryClient.invalidateQueries({
          queryKey: ["projectOverview", projectId],
        });
        queryClient.invalidateQueries({
          queryKey: ["projectSummary", projectId],
        });
      }
    },
  });
};

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      updateWorkspace(id, payload),
    onMutate: async ({ id, payload }: { id: number; payload: any }) => {
      await queryClient.cancelQueries({ queryKey: ["workspaceVersionsList"] });
      await queryClient.cancelQueries({ queryKey: ["workspacesByProject"] });
      await queryClient.cancelQueries({ queryKey: ["projectOverview"] });

      const patchWorkspaceItem = (w: any) => {
        if (Number(w.id) !== Number(id)) return w;
        return {
          ...w,
          name: payload.name ?? w.name,
          workspaceType: payload.workspaceType ?? w.workspaceType ?? w.type,
          type: payload.workspaceType ?? w.type ?? w.workspaceType,
          platform: payload.workspaceType ?? w.platform ?? w.workspaceType,
          updatedDate: new Date().toISOString(),
        };
      };

      queryClient.setQueriesData(
        { queryKey: ["workspaceVersionsList"] },
        (old: any) => {
          if (!old) return old;
          const content = Array.isArray(old?.data?.content)
            ? old.data.content
            : Array.isArray(old?.data)
              ? old.data
              : null;
          if (!content) return old;
          const updated = content.map(patchWorkspaceItem);
          return old?.data?.content
            ? { ...old, data: { ...old.data, content: updated } }
            : { ...old, data: updated };
        },
      );

      queryClient.setQueriesData(
        { queryKey: ["workspacesByProject"] },
        (old: any) => {
          if (!old) return old;
          const content = Array.isArray(old?.data?.content)
            ? old.data.content
            : Array.isArray(old?.data)
              ? old.data
              : null;
          if (!content) return old;
          const updated = content.map(patchWorkspaceItem);
          return old?.data?.content
            ? { ...old, data: { ...old.data, content: updated } }
            : { ...old, data: updated };
        },
      );

      queryClient.setQueriesData(
        { queryKey: ["projectOverview"] },
        (old: any) => {
          if (!old?.data?.workspaces) return old;
          return {
            ...old,
            data: {
              ...old.data,
              workspaces: old.data.workspaces.map(patchWorkspaceItem),
            },
          };
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaceVersionsList"] });
      queryClient.invalidateQueries({ queryKey: ["workspacesByProject"] });
      queryClient.invalidateQueries({ queryKey: ["projectOverview"] });
      queryClient.invalidateQueries({ queryKey: ["projectSummary"] });
    },
  });
};

export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteWorkspace(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: ["workspaceVersionsList"] });
      await queryClient.cancelQueries({ queryKey: ["workspacesByProject"] });
      await queryClient.cancelQueries({ queryKey: ["projectOverview"] });

      const filterWorkspaceItem = (w: any) => Number(w.id) !== Number(id);

      queryClient.setQueriesData(
        { queryKey: ["workspaceVersionsList"] },
        (old: any) => {
          if (!old) return old;
          const content = Array.isArray(old?.data?.content)
            ? old.data.content
            : Array.isArray(old?.data)
              ? old.data
              : null;
          if (!content) return old;
          const updated = content.filter(filterWorkspaceItem);
          return old?.data?.content
            ? { ...old, data: { ...old.data, content: updated } }
            : { ...old, data: updated };
        },
      );

      queryClient.setQueriesData(
        { queryKey: ["workspacesByProject"] },
        (old: any) => {
          if (!old) return old;
          const content = Array.isArray(old?.data?.content)
            ? old.data.content
            : Array.isArray(old?.data)
              ? old.data
              : null;
          if (!content) return old;
          const updated = content.filter(filterWorkspaceItem);
          return old?.data?.content
            ? { ...old, data: { ...old.data, content: updated } }
            : { ...old, data: updated };
        },
      );

      queryClient.setQueriesData(
        { queryKey: ["projectOverview"] },
        (old: any) => {
          if (!old?.data?.workspaces) return old;
          return {
            ...old,
            data: {
              ...old.data,
              workspaces: old.data.workspaces.filter(filterWorkspaceItem),
            },
          };
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaceVersionsList"] });
      queryClient.invalidateQueries({ queryKey: ["workspacesByProject"] });
      queryClient.invalidateQueries({ queryKey: ["projectOverview"] });
      queryClient.invalidateQueries({ queryKey: ["projectSummary"] });
    },
  });
};

export const useUpdateVersion = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      updateVersion(id, payload),
  });
};

export const useDeleteVersion = () => {
  return useMutation({
    mutationFn: (id: number) => deleteVersion(id),
  });
};

export const useCloneVersion = () => {
  return useMutation({
    mutationFn: (payload: any) => cloneVersion(payload),
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      updateUserById(id, payload),
  });
};
